import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  useMediaQuery,
  Typography,
  useTheme,
  Alert,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogin, setManagerDetails } from "state";
import { clearDashboardFetchCache } from "services/dashboardFetch";
import Dropzone from "react-dropzone";
import FlexBetween from "components/FlexBetween";
import { baseURL } from "services/API";
import LinearProgress from '@mui/material/LinearProgress';

const registerSchema = yup.object().shape({
  firstName: yup.string().required("required"),
  lastName: yup.string().required("required"),
  email: yup.string().email("invalid email").required("required"),
  password: yup.string().required("required"),
  location: yup.string().required("required"),
  occupation: yup.string().required("required"),
  picture: yup.string().required("required"),
});

const loginSchema = yup.object().shape({
  username: yup.string().required("required"),
  password: yup.string().required("required"),
});

const initialValuesRegister = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  location: "",
  occupation: "",
  picture: "",
};

const initialValuesLogin = {
  username: "",
  password: "",
};

const Form = () => {
  const [pageType, setPageType] = useState("login");
  const { palette } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const isLogin = pageType === "login";
  const isRegister = pageType === "register";
  const [isLoading, setIsLoading] = useState(false)
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const register = async (values, onSubmitProps) => {
    // this allows us to send form info with image
    const formData = new FormData();
    for (let value in values) {
      formData.append(value, values[value]);
    }
    formData.append("picturePath", values.picture.name);

    const savedUserResponse = await fetch(
      "http://localhost:3001/auth/register",
      {
        method: "POST",
        body: formData,
      }
    );
    const savedUser = await savedUserResponse.json();
    onSubmitProps.resetForm();

    if (savedUser) {
      setPageType("login");
    }
  };

  const login = async (values, onSubmitProps) => {
    const loggedInResponse = await fetch(`${baseURL}/accounts/authenticate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(values),
    });
    setIsLoading(true)
    const loggedIn = await loggedInResponse.json();

    onSubmitProps.resetForm();
    
    if (loggedIn.status === 200) {
      const role = loggedIn.data.role;
      const allowedRoles = ["admin", "manager", "relationship_manager"];
      if (role && allowedRoles.includes(role)) {
        clearDashboardFetchCache();
        dispatch(setLogin({
          user: loggedIn.data.user_details,
          token: loggedIn.data.token.token,
          admin: loggedIn.data.admin ?? (role === "admin" ? 1 : 0),
          role,
          relationshipManager: loggedIn.data.relationship_manager ?? null,
          assignedPropertyIds: loggedIn.data.assigned_property_ids ?? [],
        }));
        if (role === "manager" && loggedIn.data.manager) {
          dispatch(setManagerDetails(loggedIn.data.manager));
        }
        setIsLoading(false);
        navigate("/home");
      } else {
        setIsLoading(false);
        setAlertMessage("Access denied. This app is for Admin, Property Manager, or Relationship Manager only.");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
      }
    } else {
      setIsLoading(false);
      setAlertMessage("Invalid Username or Password, Please try again");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000); // Hide after 3 seconds
    }
  };

  const handleFormSubmit = async (values, onSubmitProps) => {
    if (isLogin) await login(values, onSubmitProps);
    if (isRegister) await register(values, onSubmitProps);
  };

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValuesLogin}
      validationSchema={loginSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        resetForm,
      }) => (
        <form onSubmit={handleSubmit}>
          {showAlert && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: "2rem",
                backgroundColor: "#ffebee",
                color: "#c62828"
              }}
            >
              {alertMessage}
            </Alert>
          )}
          
          <Box
            display="grid"
            gap="30px"
            gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            sx={{
              "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
            }}
          >
            <TextField
              label="Username"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.email}
              name="username"
              error={Boolean(touched.username) && Boolean(errors.username)}
              helperText={touched.username && errors.username}
              sx={{ gridColumn: "span 4", borderRadius: "2rem"}}
            />
            <TextField
              label="Password"
              type="password"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.password}
              name="password"
              error={Boolean(touched.password) && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              sx={{ gridColumn: "span 4", borderRadius: "2rem"}}
            />
          </Box>

          {/* BUTTONS */}
          <Box>
            {isLoading ? (
              <LinearProgress 
              color="warning"
              sx={{
                m: "2rem 0",
                p: "1rem",
                backgroundColor: "#141b2d",
                borderRadius: "2rem",
              }}
              />
            ) : (
              <Button
                fullWidth
                type="submit"
                sx={{
                  m: "2rem 0",
                  p: "1rem",
                  borderRadius: "2rem",
                  backgroundColor: "#141b2d",
                  color: "#FCB200",
                  "&:hover": { color: "#141b2d" },
                }}
              >
                {isLogin ? "LOGIN" : "REGISTER"}
              </Button>
            )}
            
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default Form;
